/* eslint-disable sonarjs/no-duplicate-string */
import { safeLimits } from '#~/api/billing/limits.js';
import { useCalibrate } from '#~/control/components/hooks.js';
import { Tool } from '#~/control/types.js';
import { setTool } from '#~/control/utils.js';
import type { DeviceProps } from '#~/device/components/index.js';
import { isControlDevice } from '#~/device/index.js';
import { useTemplate } from '#~/device/registry/hooks.js';
// W12 (Track 4a) — tool-gating observability. The guard below is the
// per-tool aggregation point that decides between live tool /
// <NotOnPlan> / <Uncalibrated> / <Unsupported>. We log one
// tool_disabled line per disabled state per state-transition (deduped
// via useEffect) so support can grep `tool_disabled tool=tracking
// reason=X` to see why a customer's auto-tracking was greyed out.
import { logToolDisabled } from '#~/logger/observability.js';
import { PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
// eslint-disable-next-line import/no-internal-modules
import { useRoom, useSelectedVideo } from '#~/room/hooks.js';
import {
  AutoTrackingComingSoon,
  Empty,
  NotOnPlan,
  Uncalibrated,
  Unsupported
} from './contingency.jsx';
import {
  AlignCenterIcon,
  AlignLeftIcon,
  AlignRightIcon
  //FrameMid,
  //FrameTight,
  //FrameWide
} from './icons.jsx';
import type { Props } from './types.js';
import { useWiring } from './wiring.js';
import { Button } from '@studio/design-system/button';
import { Label } from '@studio/design-system/label';
import * as Slider from '@studio/design-system/slider';
//import * as Switch from '@studio/design-system/switch';
import type { Agent } from '@studio/models/agent';
import { isCalibrated } from '@studio/models/control/calibration';
import {
  FrameAlign
  //FrameZoom,
  //ShotSafetyFallback
} from '@tryiris-ai/common.protojs/autotracking/v2/state';
import { useEffect, useState, type ReactElement } from 'react';
import { twMerge } from 'cn';

/* *@internal */
const Inner = ({
  agentId,
  deviceId,
  ...props
}: DeviceProps &
  Props & {
    /** Agent ID. */
    agentId: Agent['id'];
  }): ReactElement => {
  const {
    deadbandHeight,
    deadbandWidth,
    frameAlign,
    //@ts-ignore
    frameZoom: frameZoomEnum,
    handleChangeDeadbandHeight,
    handleChangeDeadbandWidth,
    handleChangeFrameAlign,
    //handleChangeFrameZoom,
    handleChangeTrackingSpeed,
    handleCommitDeadbandChange,
    //handleSetSafePosition,
    //handleToggleShotProtection,
    //handleToggleZoomAdaptive,
    //handleUnsetSafePosition,
    //handleViewSafePosition,
    //handleToggleCPUMode,
    //isCompositionActive,
    isCompositionEnabled,
    isLoaded,
    //safePosition,
    //setTrackingSpeedCustom,
    //shotProtection,
    //shotSafetyFallback,
    trackingSpeed
    //trackingSpeedCustom,
    //trackingSpeedMap,
    //zoomAdaptive,
    //isLowCPUModeEnabled
  } = useWiring({ agentId, deviceId });

  // TODO: Remove this once the frame zoom is implemented
  //const frameZoom = null;

  /**
   * This is a temporary solution to enable/disable the mask.
   * We are most likely going to remove this in the future, since,
   * We want the mask to be hidden by default.
   */
  //@ts-ignore
  const [isMaskEnabled, setIsMaskEnabled] = useState(true);
  //@ts-ignore
  const handleToggleMask = (checked: boolean): void => {
    setIsMaskEnabled(checked);
    if (checked) {
      window.dispatchEvent(new CustomEvent('reenableTrackingMaskUpdate'));
    } else {
      window.dispatchEvent(new CustomEvent('removeAutoTrackingMaskUpdate'));
    }
  };

  return (
    <div {...props} className={twMerge('flex gap-0 px-12')}>
      {/* Deadband controls */}
      <div className='border-r-1 border-base-100 px-28 py-16'>
        <h3 className='mb-16 text-sm'>Deadband</h3>

        <div className='mb-16'>
          <Slider.Container label='Pan Tolerance'>
            <Slider.Archetype
              disabled={!isLoaded}
              max={0.8}
              min={0.05}
              onChange={(value: number[]): void => {
                if (!value[0]) {
                  return;
                }

                handleChangeDeadbandWidth(value[0]);
              }}
              onDragEnd={(): void => {
                handleCommitDeadbandChange();
              }}
              step={0.01}
              value={[deadbandWidth]}
            />
            <Slider.Input
              disabled={!isLoaded}
              max={0.8}
              min={0.05}
              onChange={(value: string, _: React.ChangeEvent<HTMLInputElement>): void => {
                handleChangeDeadbandWidth(Number.parseInt(value, 10));
              }}
              value={Math.round((deadbandWidth ?? 0) * 100) / 100}
            />
          </Slider.Container>
          <Slider.RangeLabel>
            <Label>Less</Label>
            <Label>More</Label>
          </Slider.RangeLabel>
        </div>

        <div className='mb-16'>
          <Slider.Container label='Tilt Tolerance'>
            <Slider.Archetype
              disabled={!isLoaded}
              max={0.8}
              min={0.05}
              onChange={(value: number[]): void => {
                if (!value[0]) {
                  return;
                }

                handleChangeDeadbandHeight(value[0]);
              }}
              onDragEnd={(): void => {
                handleCommitDeadbandChange();
              }}
              step={0.01}
              value={[deadbandHeight]}
            />
            <Slider.Input
              disabled={!isLoaded}
              max={0.8}
              min={0.05}
              onChange={(value: string, _: React.ChangeEvent<HTMLInputElement>): void => {
                handleChangeDeadbandHeight(Number.parseInt(value, 10));
              }}
              value={Math.round((deadbandHeight ?? 0) * 100) / 100}
            />
          </Slider.Container>
          <Slider.RangeLabel>
            <Label>Less</Label>
            <Label>More</Label>
          </Slider.RangeLabel>
        </div>
      </div>

      {/* Speed controls */}
      <div className='border-r-1 border-base-100 px-28 py-16'>
        <h3 className='mb-16 text-sm'>Speed</h3>

        <Slider.Container label={'Acceleration'}>
          <Slider.Archetype
            disabled={!isLoaded}
            max={1}
            min={0}
            onChange={(value: number[]): void => {
              handleChangeTrackingSpeed(value[0] ?? 0);
            }}
            step={0.01}
            value={[trackingSpeed ?? 0]}
          />
          <Slider.Input
            disabled={!isLoaded}
            max={1}
            min={0}
            onChange={(value: string, _: React.ChangeEvent<HTMLInputElement>): void => {
              handleChangeTrackingSpeed(Math.max(0.01, Number.parseInt(value, 10)));
            }}
            value={Math.round((trackingSpeed ?? 0) * 100) / 100}
          />
        </Slider.Container>
        <Slider.RangeLabel>
          <Label>Less</Label>
          <Label>More</Label>
        </Slider.RangeLabel>
      </div>

      {/* Composition controls */}
      <div className='border-r-1 border-base-100 px-28 py-16'>
        <h3 className='mb-16 text-sm'>Composition</h3>

        <div className='mb-16'>
          <h3 className='text-sm text-muted'>Alignment</h3>
          <div className='flex'>
            {/* Align Left */}
            <Button
              className={twMerge(
                'min-w-[78px] justify-start border-none bg-base-100 px-6 text-xs',
                !(!isLoaded || !isCompositionEnabled) &&
                  frameAlign === FrameAlign.FRAME_ALIGN_LEFT &&
                  'bg-primary text-white hover:enabled:bg-primary-highlight'
              )}
              disabled={!isLoaded || !isCompositionEnabled}
              onClick={(): void => {
                handleChangeFrameAlign(FrameAlign.FRAME_ALIGN_LEFT);
              }}
              variant='tertiary'
            >
              <AlignLeftIcon
                className='w-[29px]'
                color={
                  !(!isLoaded || !isCompositionEnabled) &&
                  frameAlign === FrameAlign.FRAME_ALIGN_LEFT
                    ? '#FFF'
                    : undefined
                }
              />
              <span>Left</span>
            </Button>
            {/* Align Center */}
            <Button
              className={twMerge(
                'ml-12 min-w-[78px] justify-start border-none bg-base-100 px-6 text-xs',
                !(!isLoaded || !isCompositionEnabled) &&
                  frameAlign === FrameAlign.FRAME_ALIGN_CENTER &&
                  'bg-primary text-white hover:enabled:bg-primary-highlight'
              )}
              color={
                !(!isLoaded || !isCompositionEnabled) &&
                frameAlign === FrameAlign.FRAME_ALIGN_CENTER
                  ? '#FFF'
                  : undefined
              }
              disabled={!isLoaded || !isCompositionEnabled}
              onClick={(): void => {
                handleChangeFrameAlign(FrameAlign.FRAME_ALIGN_CENTER);
              }}
              variant='tertiary'
            >
              <AlignCenterIcon className='w-[29px]' />
              <span>Center</span>
            </Button>
            {/* Align Right */}
            <Button
              className={twMerge(
                'ml-12 min-w-[78px] justify-start border-none bg-base-100 px-6 text-xs',
                !(!isLoaded || !isCompositionEnabled) &&
                  frameAlign === FrameAlign.FRAME_ALIGN_RIGHT &&
                  'bg-primary text-white hover:enabled:bg-primary-highlight'
              )}
              color={
                !(!isLoaded || !isCompositionEnabled) && frameAlign === FrameAlign.FRAME_ALIGN_RIGHT
                  ? '#FFF'
                  : undefined
              }
              disabled={!isLoaded || !isCompositionEnabled}
              onClick={(): void => {
                handleChangeFrameAlign(FrameAlign.FRAME_ALIGN_RIGHT);
              }}
              variant='tertiary'
            >
              <AlignRightIcon className='w-[29px]' />
              <span>Right</span>
            </Button>
          </div>
        </div>
        {/*
          <h3 className='text-sm text-muted'>Shot type</h3>

          <div className='flex'>
            <Button
              className={twMerge(
                'min-w-[78px] justify-start border-none bg-base-100 px-6 text-xs',
                frameZoom === FrameZoom.FRAME_ZOOM_TIGHT &&
                  'bg-primary text-white hover:enabled:bg-primary-highlight'
              )}
              disabled={!isLoaded || !isCompositionEnabled}
              onClick={(): void => {
                handleChangeFrameZoom(FrameZoom.FRAME_ZOOM_TIGHT);
              }}
              variant='tertiary'
            >
              <FrameTight
                className='w-[29px]'
                color={frameZoom === FrameZoom.FRAME_ZOOM_TIGHT ? '#FFF' : undefined}
              />
              <span>Tight</span>
            </Button>
            <Button
              className={twMerge(
                'ml-12 min-w-[78px] justify-start border-none bg-base-100 px-6 text-xs',
                frameZoom === FrameZoom.FRAME_ZOOM_MID &&
                  'bg-primary text-white hover:enabled:bg-primary-highlight'
              )}
              disabled={!isLoaded || !isCompositionEnabled}
              onClick={(): void => {
                handleChangeFrameZoom(FrameZoom.FRAME_ZOOM_MID);
              }}
              variant='tertiary'
            >
              <FrameMid
                className='w-[29px]'
                color={frameZoom === FrameZoom.FRAME_ZOOM_MID ? '#FFF' : undefined}
              />
              <span>Mid</span>
            </Button>
            <Button
              className={twMerge(
                'ml-12 min-w-[78px] justify-start border-none bg-base-100 px-6 text-xs',
                frameZoom === FrameZoom.FRAME_ZOOM_WIDE &&
                  'bg-primary text-white hover:enabled:bg-primary-highlight'
              )}
              disabled={!isLoaded || !isCompositionEnabled}
              onClick={(): void => {
                handleChangeFrameZoom(FrameZoom.FRAME_ZOOM_WIDE);
              }}
              variant='tertiary'
            >
              <FrameWide
                className='w-[29px]'
                color={frameZoom === FrameZoom.FRAME_ZOOM_WIDE ? '#FFF' : undefined}
              />
              <span>Wide</span>
            </Button>
          </div>*/}
      </div>

      {/*
        <div className='border-r-1 border-base-100 px-28 py-16'>
          <h3 className='mb-16 text-sm'>Zoom</h3>

          <Switch.Container className='mb-16 w-[200px]' label='Adaptive Zoom'>
            <Switch.Archetype
              checked={!!zoomAdaptive || isCompositionActive}
              disabled={!isLoaded || isCompositionActive}
              onClick={handleToggleZoomAdaptive}
            />
          </Switch.Container>

          <Switch.Container className='mt-8 min-w-[200px]' label='Toggle Mask'>
            <Switch.Archetype
              checked={!!isMaskEnabled}
              disabled={!isLoaded || !isCompositionEnabled}
              onCheckedChange={handleToggleMask}
            />
          </Switch.Container>
          
        </div>
        */}

      {/*
        <div className='border-r-1 border-base-100 px-28 py-16'>
          <h3 className='mb-16 text-sm'>Safety</h3>

          <Switch.Container className='mb-16 min-w-[200px]' label='Shot protection'>
            <Switch.Archetype
              checked={!!shotProtection}
              disabled={!isLoaded}
              onClick={handleToggleShotProtection}
            />
          </Switch.Container>

          {shotSafetyFallback === ShotSafetyFallback.SHOT_SAFETY_FALLBACK_SAFE_POSITION && (
            <div className='mt-8'>
              {safePosition !== undefined && (
                <div className='flex'>
                  <Button
                    className='flex-1'
                    disabled={!isLoaded || !shotProtection}
                    onClick={handleViewSafePosition}
                    size='sm'
                    variant='tertiary'
                  >
                    View Current
                  </Button>
                  <Button
                    className='ml-4 flex-1'
                    disabled={!isLoaded || !shotProtection}
                    onClick={handleUnsetSafePosition}
                    size='sm'
                    variant='tertiary'
                  >
                    Delete
                  </Button>
                </div>
              )}

              {safePosition === undefined && (
                <Button
                  className='w-full border-error'
                  disabled={!isLoaded}
                  onClick={handleSetSafePosition}
                  size='sm'
                  variant='tertiary'
                >
                  Set Safe Position
                </Button>
              )}
            </div>
          )}

          <Switch.Container className='mt-8 min-w-[200px]' label='Low CPU Mode'>
            <Switch.Archetype
              checked={!!isLowCPUModeEnabled}
              disabled={!isLoaded || isCompositionEnabled}
              onClick={(): void => {
                handleToggleCPUMode();
              }}
            />
          </Switch.Container>

        </div>*/}
    </div>
  );
};

/** @internal Interim `Guard` component. */
const Guard = ({
  deviceId,
  ...props
}: DeviceProps & Props & { canAutoTrackPlan: boolean }): ReactElement => {
  const calibrate = useCalibrate();

  // Auto-tracking only works on _calibrated_ control devices.
  const [agentId, bSupported, bCalibrated, modelId] = useRoom((µ) => {
    const device = µ.devices[deviceId];

    return [
      device?.agentId,
      isControlDevice(device),
      isCalibrated(device?.control?.calibration?.status),
      device?.modelId
    ];
  });

  // ENG-4495 C5/D3: resolve the model template for the driver-identity bit. Tri-state, permissive
  // on absence — only a LOADED generic === true forces the Unsupported path below.
  const template = useTemplate(modelId);
  const bGeneric = template?.model?.generic === true;

  const [canAutoTrackPlan, setCanAutoTrackPlan] = useState(false);

  useEffect(() => {
    const fn = async () => {
      const currentLimits = await safeLimits();

      setCanAutoTrackPlan((draft) => {
        const value = currentLimits?.limits?.hasAutoTracking ?? false;

        if (value && !draft) {
          setTool(Tool.Tracking);
        }

        return value;
      });
    };

    const interval = setInterval(fn, 1000); // Refresh every second

    fn();

    return () => clearInterval(interval);
  }, [setCanAutoTrackPlan]);

  // W12 — emit one tool_disabled line per gating reason transition.
  // useEffect with the gating triple as deps ensures we fire ONCE per
  // state change (not on every render) per the helper's contract in
  // logger/observability.ts. The first effect run after mount also
  // emits, which is intentional: support wants to see "this customer
  // entered the room with tracking disabled because reason=X".
  useEffect(() => {
    if (!canAutoTrackPlan) {
      logToolDisabled('tracking', 'plan_limit');
    } else if (bGeneric) {
      // ENG-4495 US5: distinct grep-able reason for the driverless-model block, kept ahead of the
      // supported/calibrated reasons so support sees the real cause (D4: generic beats calibration).
      logToolDisabled('tracking', 'driver_generic');
    } else if (!bSupported) {
      logToolDisabled('tracking', 'driver_unsupported');
    } else if (!bCalibrated) {
      logToolDisabled('tracking', 'calibration_missing');
    }
    // No-op when fully enabled — we only log the disabled state.
  }, [canAutoTrackPlan, bGeneric, bSupported, bCalibrated]);

  if (!canAutoTrackPlan) {
    return <NotOnPlan />;
  }

  // ENG-4495 C5/D4: a driverless model can never be auto-tracked — render Unsupported and expose
  // ZERO calibrate affordance, never <Uncalibrated> and its live Calibrate button (that button is
  // the V3 hole that re-opened ENG-3367's closed path). Generic beats the supported/calibrated logic.
  if (bGeneric) {
    return <Unsupported />;
  }

  return bSupported ? (
    bCalibrated ? (
      <Inner agentId={agentId!} deviceId={deviceId} {...props} />
    ) : (
      <Uncalibrated
        onCalibrate={(): void => {
          calibrate(deviceId);
        }}
      />
    )
  ) : (
    <Unsupported />
  );
};

/** `<AutoTracking>` component. */
export const AutoTracking = (props: Props): ReactElement => {
  const [deviceId] = useSelectedVideo();

  const [canAutoTrackPlan, setCanAutoTrackPlan] = useState(true);
  const { enabled: hasAutoTrackingFeature, loading: isLoadingFeature } = useFeatureFlag(
    PostHogFeatureFlag.AUTOTRACKING,
    { defaultValue: false }
  );

  useEffect(() => {
    (async () => {
      const currentLimits = await safeLimits();
      setCanAutoTrackPlan(currentLimits?.limits?.hasAutoTracking ?? false);
    })();
  }, [setCanAutoTrackPlan]);

  // W12 — feature-flag gating observability. Fires once per state
  // change (not per render) — the effect dep is the boolean tuple so a
  // flag flip from off->on emits exactly once.
  useEffect(() => {
    if (!isLoadingFeature && canAutoTrackPlan && !hasAutoTrackingFeature) {
      logToolDisabled('tracking', 'feature_flag_off');
    }
  }, [isLoadingFeature, canAutoTrackPlan, hasAutoTrackingFeature]);

  // Wait for feature flag to load before rendering
  if (isLoadingFeature) {
    return <div />; // Or a loading spinner if desired
  }

  if (canAutoTrackPlan && !hasAutoTrackingFeature) {
    return <AutoTrackingComingSoon />;
  }

  return deviceId ? (
    <Guard {...props} deviceId={deviceId} canAutoTrackPlan={canAutoTrackPlan} />
  ) : (
    <Empty />
  );
};
